import Immutable from 'immutable'
import parse from 'parse-link-header'

import * as types from '../../actions/coach/types'

// Read a field from snap data that might be either a plain object (the action
// payload) or an Immutable.Map (when read back from state.clientSnapData).
const readSnapField = (snapData, key) =>
  Immutable.Map.isMap(snapData) ? snapData.get(key) : snapData[key]

// Merge vatMass / metabolicAgeYears from snap data into clientReport's
// wellnessMetrics, skipping null/undefined so a partial snap response can't
// clobber values supplied by the wellness report itself.
const mergeSnapIntoClientReport = (state, snapData) => {
  const clientReport = state.get('clientReport')
  if (!clientReport || !snapData) return state

  const updates = {}
  const vatMass = readSnapField(snapData, 'vatMass')
  const metabolicAgeYears = readSnapField(snapData, 'metabolicAgeYears')
  if (vatMass != null) updates.vatMass = vatMass
  if (metabolicAgeYears != null) updates.metabolicAgeYears = metabolicAgeYears
  if (Object.keys(updates).length === 0) return state

  const currentMetrics = clientReport.get('wellnessMetrics') || Immutable.Map()
  return state.setIn(
    ['clientReport', 'wellnessMetrics'],
    currentMetrics.merge(updates)
  )
}

const reducers = {
  // Fetch all clients
  [types.FETCH_CLIENTS]: (state) => state.set('loading', true),
  [types.FETCH_CLIENTS_SUCCESS]: (state, action) => {
    return state
      .set('clients', Immutable.fromJS(action.payload.data))
      .set('loading', false)
  },
  [types.FETCH_CLIENTS_FAILURE]: (state) => state.set('loading', false),

  [types.FETCH_CLIENT_DATA]: (state) => state.set('loading', true),
  [types.FETCH_CLIENT_DATA_SUCCESS]: (state, action) => {
    return state
      .set('client', Immutable.fromJS(action.payload.data))
      .set('loading', false)
  },
  [types.FETCH_CLIENT_DATA_FAILURE]: (state) => state.set('loading', false),

  // Updating client's measurements
  [types.UPDATE_CLIENT_MEASUREMENTS]: (state) =>
    state.set('measurementLoading', true),
  [types.UPDATE_CLIENT_MEASUREMENTS_SUCCESS]: (state, action) => {
    const index = state
      .get('client')
      .findIndex((item) => item.get('id') === action.meta.recordId)
    const updatedList = state.get('client').update(index, (item) => {
      return item.set('wellnessMetrics', Immutable.fromJS(action.payload.data))
    })

    return state
      .set('client', updatedList)
      .set('measurementLoading', false)
      .set('message', 'success')
  },
  [types.UPDATE_CLIENT_MEASUREMENTS_FAILURE]: (state) =>
    state.set('measurementLoading', false).set('message', 'failure'),

  [types.UPDATE_CLIENT_ADDITIONAL]: (state) =>
    state.set('metricsLoading', true),
  [types.UPDATE_CLIENT_ADDITIONAL_SUCCESS]: (state, action) => {
    const index = state
      .get('client')
      .findIndex((item) => item.get('id') === action.meta.recordId)
    const updatedList = state.get('client').update(index, (item) => {
      return item.setIn(
        ['wellnessMetrics', 'additional'],
        Immutable.fromJS(action.payload.data)
      )
    })

    return state
      .set('client', updatedList)
      .set('metricsLoading', false)
      .set('message', 'success')
  },
  [types.UPDATE_CLIENT_ADDITIONAL_FAILURE]: (state) =>
    state.set('metricsLoading', false).set('message', 'failure'),

  // Manage client permissions
  [types.MANAGE_CLIENT]: (state) => state.set('message', 'fetching'),
  [types.MANAGE_CLIENT_SUCCESS]: (state, action) => {
    const { userId, accessRequestDate } = action.payload.data
    const newClientRecords = []

    state.get('clientRecords').forEach((clientRecord) => {
      let newClientRecord = clientRecord

      if (userId === clientRecord.get('userId')) {
        newClientRecord = clientRecord.set(
          'accessRequestDate',
          accessRequestDate
        )
      }

      newClientRecords.push(newClientRecord)
    })

    return state
      .set('clientRecords', Immutable.fromJS(newClientRecords))
      .set('message', null)
  },
  [types.MANAGE_CLIENT_FAILURE]: (state) => state.set('message', 'failure'),

  // Client Records
  [types.FETCH_CLIENT_RECORDS]: (state) => state.set('loading', true),
  [types.FETCH_CLIENT_RECORDS_SUCCESS]: (state, action) => {
    const pageInfo = parse(action.payload.headers.link)
    return state
      .set('clientRecords', Immutable.fromJS(action.payload.data))
      .set('pageInfo', Immutable.fromJS(pageInfo))
      .set('loading', false)
  },

  [types.FETCH_CLIENT_RECORDS_FAILURE]: (state) => state.set('loading', false),

  // Client Reports
  [types.FETCH_CLIENT_REPORT]: (state) =>
    state
      .set('loading', true)
      .set('pageError', false)
      // Drop any cached snap data so a new report fetch can't merge the
      // previous scan's vatMass/metabolicAgeYears into a different scan.
      .set('clientSnapData', null),
  [types.FETCH_CLIENT_REPORT_SUCCESS]: (state, action) => {
    let newState = state
      .set('clientReport', Immutable.fromJS(action.payload.data))
      .set('loading', false)
      .set('pageError', false)

    // If snap details landed before the report (or are still cached from
    // before a remount), fold their vatMass / metabolicAgeYears back into
    // wellnessMetrics so the snap values aren't lost when clientReport is
    // replaced. Order-of-arrival doesn't matter anymore.
    const snapData = newState.get('clientSnapData')
    if (snapData) {
      newState = mergeSnapIntoClientReport(newState, snapData)
    }

    return newState
  },
  [types.FETCH_CLIENT_REPORT_FAILURE]: (state) =>
    state.set('loading', false).set('pageError', true),

  // Updating client current/baseline scans
  // [types.UPDATE_CLIENT_CURRENT]: (state, action) => state.set('currentScan', action.payload),
  // [types.UPDATE_CLIENT_BASELINE]: (state, action) => state.set('baselineScan', action.payload),

  // PARQ
  [types.FETCH_PARQ_DATA]: (state) =>
    state.set('loading', true).set('pageError', false),
  [types.FETCH_PARQ_DATA_SUCCESS]: (state, action) => {
    return state
      .set('parqData', Immutable.fromJS(action.payload.data))
      .set('loading', false)
      .set('pageError', false)
  },
  [types.FETCH_PARQ_DATA_FAILURE]: (state) =>
    state.set('loading', false).set('pageError', true),

  // Editing PARQ
  [types.UPDATE_PARQ_DATA]: (state) => state.set('loadingPARQUpdate', true),
  [types.UPDATE_PARQ_DATA_SUCCESS]: (state, action) => {
    return state
      .set('parqData', Immutable.fromJS(action.payload.data))
      .set('loadingPARQUpdate', false)
      .set('message', 'success')
  },
  [types.UPDATE_PARQ_DATA_FAILURE]: (state) =>
    state.set('loadingPARQUpdate', false).set('message', 'failure'),

  // PARQ Interest
  [types.FETCH_PARQ_INTEREST]: (state) => state.set('loadingPARQUpdate', true),
  [types.FETCH_PARQ_INTEREST_SUCCESS]: (state, action) => {
    return state
      .set('clientPARQInterest', Immutable.fromJS(action.payload.data))
      .set('loadingPARQUpdate', false)
  },
  [types.FETCH_PARQ_INTEREST_FAILURE]: (state) =>
    state.set('loadingPARQUpdate', false),

  // Client Summary
  [types.FETCH_CLIENT_SUMMARY]: (state) => state.set('loading', true),
  [types.FETCH_CLIENT_SUMMARY_SUCCESS]: (state, action) => {
    return state
      .set('clientSummary', Immutable.fromJS(action.payload.data))
      .set('loading', false)
  },
  [types.FETCH_CLIENT_SUMMARY_FAILURE]: (state) => state.set('loading', false),

  [types.CLEAR_MESSAGE]: (state) => state.set('message', null),

  [types.RESET_CLIENT_DATA]: (state) => state.set('client', null),

  [types.RESET_CLIENT_REPORT]: (state) =>
    state
      .set('clientReport', null)
      .set('clientSummary', null)
      .set('clientSnapData', null),

  // Client Snap Details (HW v7 - vatMass, metabolicAgeYears)
  [types.FETCH_CLIENT_SNAP_DETAILS_SUCCESS]: (state, action) => {
    const snapData = action.payload.data
    if (!snapData) return state

    const newState = state.set('clientSnapData', Immutable.fromJS(snapData))
    return mergeSnapIntoClientReport(newState, snapData)
  },
}

const defaultState = new Immutable.Map({
  loading: false,
  parqData: new Immutable.List(),
  loadingPARQUpdate: false,
  metricsLoading: false,
  measurementLoading: false,
  message: null,
  pageError: false,
  // currentScan: null,
  // baselineScan: null
})

export default function (state = defaultState, action) {
  if (action && typeof reducers[action.type] === 'function') {
    return reducers[action.type](state, action)
  }

  return state
}
